import React, { createContext, useContext, useEffect, useState } from "react";
import { MyContext } from "../context/Context";
import { urlLinks } from "../UrlLinks";
import axios from "axios";

//////*****  Dynamically load AWS SDK and CryptoJS ***///////
const loadAWS = async () => {
  const AWS = (await import("aws-sdk")).default;
  return AWS;
};

const loadCryptoJS = async () => {
  const CryptoJS = (await import("crypto-js")).default;
  return CryptoJS;
};
/****----------------------------------------------------- */

export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  ///////////******** AWS ID *//////////////

  const userPoolId = process.env.REACT_APP_AWS_COGNITO_POOL_ID;
  const clientId = process.env.REACT_APP_AWS_COGNITO_CLIENT_ID;
  const clientSecret = process.env.REACT_APP_AWS_CLIENT_SECRET_KEY;
  const region = process.env.REACT_APP_AWS_REGION;
  const accessKeyId = process.env.REACT_APP_AWS_ACCESS_KEY_ID;
  const secretAccessKey = process.env.REACT_APP_AWS_SECRET_ACCESS_KEY;

  /****----------------------------------------------------- */

  const { mobileNumber, pswd, workerDetails, setWorkerDetails } =
    useContext(MyContext);
  const [navigatePage, setNavigatePage] = useState("");
  const [AWS, setAWS] = useState(null);
  const [cognito, setCognito] = useState(null);
  const [CryptoJS, setCryptoJS] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [otpCode, setOtpCode] = useState();

  // Configure AWS SDK
  const configureAWS = async () => {
    if (!AWS) {
      const AWS = await loadAWS();
      setAWS(AWS);
      AWS.config.update({
        region: region,
        accessKeyId: accessKeyId,
        secretAccessKey: secretAccessKey,
      });
      // Create a CognitoIdentityServiceProvider instance
      if (!cognito) {
        setCognito(new AWS.CognitoIdentityServiceProvider());
      }
    } else {
      AWS.config.update({
        region: region,
        accessKeyId: accessKeyId,
        secretAccessKey: secretAccessKey,
      });
    }
  };

  // Function to generate SECRET_HASH
  const generateSecretHash = async () => {
    if (!CryptoJS) {
      const CryptoJSLoaded = await loadCryptoJS();
      const message = mobileNumber + clientId;
      const hash = CryptoJSLoaded.HmacSHA256(message, clientSecret);
      setCryptoJS(CryptoJSLoaded);
      return CryptoJSLoaded.enc.Base64.stringify(hash);
    }
    const message = mobileNumber + clientId;
    const hash = CryptoJS.HmacSHA256(message, clientSecret);
    return CryptoJS.enc.Base64.stringify(hash);
  };

  // Function to generate SECRET_HASH_PASSWORD
  const generateSecretHashPassword = async (password) => {
    if (!CryptoJS) {
      const CryptoJSLoaded = await loadCryptoJS();
      setCryptoJS(CryptoJSLoaded);
      return CryptoJSLoaded.SHA256(password).toString(CryptoJSLoaded.enc.Hex);
    }
    return CryptoJS.SHA256(password).toString(CryptoJS.enc.Hex);
  };

  // Function to confirm changed password
  const handleConfirmPassword = async (password) => {
    await configureAWS(); // Ensure AWS is configured
    const secretHash = await generateSecretHash();
    const params = {
      ClientId: clientId,
      Username: mobileNumber,
      ConfirmationCode: otpCode,
      Password: password,
      SecretHash: secretHash,
    };

    try {
      await cognito.confirmForgotPassword(params).promise();
      alert("Password changed successfully");
    } catch (error) {
      console.error("Error during password confirmation:", error);
    }
  };

  // Function to send OTP when forgot password
  const handleForgotPassword = async () => {
    await configureAWS(); // Ensure AWS is configured
    const secretHash = await generateSecretHash();
    const params = {
      ClientId: clientId,
      Username: mobileNumber,
      SecretHash: secretHash,
    };

    try {
      await cognito.forgotPassword(params).promise();
      return true;
    } catch (error) {
      console.error("Error during forgot password:", error);
      return false;
    }
  };

  // Function to create a new password
  const handleChangePassword = async (password) => {
    await configureAWS(); // Ensure AWS is configured
    const params = {
      UserPoolId: userPoolId,
      Username: mobileNumber,
      Password: password,
      Permanent: true,
    };

    try {
      await cognito.adminSetUserPassword(params).promise();
      alert("Password changed successfully");
    } catch (error) {
      if (error.name === "InvalidPasswordException") {
        alert(error.message);
      } else {
        console.error("Error during password change:", error);
      }
      throw error;
    }
  };

  // Function to validate OTP
  const validateOtp = async () => {
    const secretHash = await generateSecretHash();
    const params = {
      ClientId: clientId,
      Username: mobileNumber,
      ConfirmationCode: otpCode,
      SecretHash: secretHash,
    };

    try {
      // Validate the OTP
      await cognito.confirmSignUp(params).promise();
      setNavigatePage("createPassword");
      return true;
    } catch (error) {
      console.log("Error in validating OTP =>", error);
      return false;
    }
  };

  const sendOtp = async () => {
    await configureAWS(); // Ensure AWS is configured
    const secretHash = await generateSecretHash();
    const params = {
      ClientId: clientId,
      Username: mobileNumber,
      SecretHash: secretHash,
    };

    try {
      await cognito.resendConfirmationCode(params).promise();
      return true;
    } catch (error) {
      console.log("error=>", error.name);
      setErrorType(error.name);
      return false;
    }
  };

  const signUpUser = async () => {
    await configureAWS(); // Ensure AWS is configured
    const hashedPassword = await generateSecretHashPassword(mobileNumber);
    const secretHash = await generateSecretHash();
    const params = {
      ClientId: clientId,
      Username: mobileNumber,
      Password: hashedPassword,
      SecretHash: secretHash,
      UserAttributes: [
        {
          Name: "email",
          Value: `${mobileNumber}@fkokaygo.in`,
        },
        {
          Name: "phone_number",
          Value: `+91${mobileNumber}`,
        },
      ],
    };

    try {
      await cognito.signUp(params).promise();
      return true;
    } catch (error) {
      if (error.code === "UsernameExistsException") {
        console.log("User already exists");
      } else {
        console.error("Error signing up user:", error);
      }
    }
  };

  // Example of sending OTP and navigating the user
  const handleOtpGeneration = async () => {
    sendOtp()
      .then((session) => {
        console.log("session", session);
        if (!session) return;
        setNavigatePage("otpVerify");
      })
      .catch((err) => console.log("errr==>", err));
  };

  const checkMobileNumber = async () => {
    await configureAWS(); // Ensure AWS is configured
    const params = {
      UserPoolId: userPoolId,
      Filter: `phone_number = "+91${mobileNumber}"`,
    };
    try {
      const data = await cognito.listUsers(params).promise();
      if (data.Users.length > 0) {
        const user = data.Users[0];
        const status = user.UserStatus;
        return status === "CONFIRMED";
      } else {
        return false; // Phone number not found
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      return false;
    }
  };

  // Function to login using mobile and password
  const passwordSubmit = async () => {
    await configureAWS(); // Configure AWS SDK

    try {
      const secretHash = await generateSecretHash();

      // Create a Cognito service object
      const cognito = new AWS.CognitoIdentityServiceProvider();

      // Parameters for the adminInitiateAuth method
      const params = {
        AuthFlow: "USER_PASSWORD_AUTH",
        ClientId: clientId,
        AuthParameters: {
          USERNAME: mobileNumber,
          PASSWORD: pswd,
          SECRET_HASH: secretHash,
        },
      };

      // Authenticate user
      const result = await cognito.initiateAuth(params).promise();
      return result.AuthenticationResult.AccessToken;
    } catch (error) {
      console.error("Error during password submission:", error);
    }
  };

  const getWorkerDetails = async () => {
    try {
      const workerResponse = await axios.get(
        `${urlLinks.baseUrl}/${
          urlLinks.workerV2.getWorkerDetails
        }?user_id=${localStorage.getItem("userId")}`
      );

      if (workerResponse.data.code === 1000) {
        let data = workerResponse.data.response.content[0];

        localStorage.setItem("phone", data.phoneNumber);
        localStorage.setItem(
          "name",
          `${data.firstName}${" "}${data.lastName || ""}`
        );
        localStorage.setItem("ogId", data.okaygoId);
        let profilePhoto = "";
        if (!data.profilePhoto) {
          localStorage.setItem("profilePhoto", "");
          profilePhoto = localStorage.getItem("profilePhoto");
        } else {
          localStorage.setItem("profilePhoto", data.profilePhoto);
          profilePhoto = localStorage.getItem("profilePhoto");
        }

        setWorkerDetails({
          ...workerDetails,
          name: `${data.firstName}${" "}${data.lastName || ""}`,
          ogId: data.okaygoId,
          phone: data.phoneNumber,
          profilePhoto: profilePhoto,
        });
        setNavigatePage("taskPage");
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (errorType === "UserNotFoundException") {
      signUpUser().then((session) => {
        console.log("session signup", session);
        if (!session) return;
        setNavigatePage("otpVerify");
      });
    }
  }, [errorType]);

  useEffect(() => {
    configureAWS(); // Ensure AWS and Cognito are initialized
  }, [AWS, cognito]);

  return (
    <AuthContext.Provider
      value={{
        checkMobileNumber,
        handleOtpGeneration,
        navigatePage,
        validateOtp,
        setOtpCode,
        handleChangePassword,
        handleForgotPassword,
        handleConfirmPassword,
        passwordSubmit,
        otpCode,
        getWorkerDetails,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
